import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  Divider,
  Notification,
  Space,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { collection, getDocs, query, where, QuerySnapshot, DocumentData } from "firebase/firestore";
import { PageHeader, ScrollableTabs } from "../components/shared";

import { db } from "../firebase";

import { InfoCircle } from "tabler-icons-react";
import { TankTable } from "./tables/tank-table";
import { VehicleTable } from "./tables/vehicle-table";
import { SupplierTable } from "./tables/supplier-table";
import { VehicleOperatorsTable } from "./tables/vehicle-operator-table";
import { ActivitiesTable } from "./tables/activities-table";
import { ProductsTable } from "./tables/products-table";
import { ZonesTable } from "./tables/zones-table";
import { VehicleGroupsTable } from "./tables/vehicle-group-table";
import { FuelTypesTable } from "./tables/fuel-types-table";
import {
  Tank,
  Supplier,
  Vehicle,
  VehicleGroup,
  FleetOperator,
  Activity,
  Product,
  FuelType,
  Zone,
} from "@captiva/shared-types";

export interface AssetsProps {
  companyId: string;
  auth: {
    claims: {
      admin?: boolean;
      companyId?: string;
      user_id?: string;
    };
  } | null | undefined;
}

export interface AssetTabInfo {
  index: number;
  tabKey: string;
}

export const ASSET_TAB_KEYS = [
  "vehicles",
  "vehicleOperators",
  "vehicleGroups",
  "tanks",
  "suppliers",
  "activities",
  "products",
  "fuelTypes",
  "zones",
] as const;

export const DOMAIN_TO_TAB_MAP: Record<string, AssetTabInfo> = {
  // Products
  product: { index: 6, tabKey: "products" },
  products: { index: 6, tabKey: "products" },

  // Fuel Types
  fueltype: { index: 7, tabKey: "fuelTypes" },
  fueltypes: { index: 7, tabKey: "fuelTypes" },
  fuel_type: { index: 7, tabKey: "fuelTypes" },
  fuel_types: { index: 7, tabKey: "fuelTypes" },
  fuel: { index: 7, tabKey: "fuelTypes" },

  // Vehicles / Fleet
  vehicle: { index: 0, tabKey: "vehicles" },
  vehicles: { index: 0, tabKey: "vehicles" },
  fleet: { index: 0, tabKey: "vehicles" },

  // Fleet Operators
  operator: { index: 1, tabKey: "vehicleOperators" },
  operators: { index: 1, tabKey: "vehicleOperators" },
  vehicleoperator: { index: 1, tabKey: "vehicleOperators" },
  vehicleoperators: { index: 1, tabKey: "vehicleOperators" },
  fleetoperator: { index: 1, tabKey: "vehicleOperators" },
  fleetoperators: { index: 1, tabKey: "vehicleOperators" },

  // Vehicle Groups
  group: { index: 2, tabKey: "vehicleGroups" },
  groups: { index: 2, tabKey: "vehicleGroups" },
  vehiclegroup: { index: 2, tabKey: "vehicleGroups" },
  vehiclegroups: { index: 2, tabKey: "vehicleGroups" },
  fleetgroup: { index: 2, tabKey: "vehicleGroups" },
  fleetgroups: { index: 2, tabKey: "vehicleGroups" },

  // Tanks
  tank: { index: 3, tabKey: "tanks" },
  tanks: { index: 3, tabKey: "tanks" },

  // Suppliers
  supplier: { index: 4, tabKey: "suppliers" },
  suppliers: { index: 4, tabKey: "suppliers" },

  // Activities
  activity: { index: 5, tabKey: "activities" },
  activities: { index: 5, tabKey: "activities" },

  // Zones
  zone: { index: 8, tabKey: "zones" },
  zones: { index: 8, tabKey: "zones" },
  areaforfleetuse: { index: 8, tabKey: "zones" },
};

export function resolveAssetTab(param: string | null | undefined): AssetTabInfo | null {
  if (!param) return null;
  const clean = param.trim().toLowerCase();
  if (DOMAIN_TO_TAB_MAP[clean]) {
    return DOMAIN_TO_TAB_MAP[clean];
  }
  const numeric = parseInt(clean, 10);
  if (!isNaN(numeric) && numeric >= 0 && numeric < ASSET_TAB_KEYS.length) {
    return { index: numeric, tabKey: ASSET_TAB_KEYS[numeric] };
  }
  return null;
}

const TabItem = ScrollableTabs.Tab;

export function Assets({ companyId, auth }: AssetsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [message, setMessage] = useState("");
  const [tanks, setTanks] = useState<Tank[]>([]);
  const [fetchedTanks, setFetchedTanks] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [fetchedSuppliers, setFetchedSuppliers] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [fetchedVehicles, setFetchedVehicles] = useState(true);
  const [vehicleGroups, setVehicleGroups] = useState<VehicleGroup[]>([]);
  const [fetchedVehicleGroups, setFetchedVehicleGroups] = useState(true);
  const [operators, setOperators] = useState<FleetOperator[]>([]);
  const [fetchedOperators, setFetchedOperators] = useState(true);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [fetchedActivities, setFetchedActivities] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [fetchedProducts, setFetchedProducts] = useState(true);
  const [fuelTypes, setFuelTypes] = useState<FuelType[]>([]);
  const [fetchedFuelTypes, setFetchedFuelTypes] = useState(true);
  const [zones, setZones] = useState<Zone[]>([]);
  const [fetchedZones, setFetchedZones] = useState(true);

  const initialTab = useMemo(() => {
    const resolved = resolveAssetTab(searchParams.get("tab")) ?? resolveAssetTab(searchParams.get("domain"));
    return resolved ? resolved.index : 0;
  }, []);

  const [activeTab, setActiveTab] = useState<number>(initialTab);

  const resolvedAuth = auth
    ? {
        uid: auth.claims.user_id || "",
        claims: auth.claims,
      }
    : null;

  const triggerFetchForTab = useCallback((tabKey: string) => {
    if (tabKey === "tanks" && !tanks?.length) {
      setFetchedTanks(false);      
      setFetchedFuelTypes(false);    
    } else if (tabKey === "suppliers" && !suppliers?.length) {
      setFetchedSuppliers(false);
    } else if (tabKey === "vehicles" && !vehicles?.length) {
      setFetchedVehicleGroups(false);
      setFetchedVehicles(false);
      setFetchedFuelTypes(false);
    } else if (tabKey === "vehicleGroups" && !vehicleGroups?.length) {
      setFetchedVehicleGroups(false);
    } else if (tabKey === "vehicleOperators" && !operators?.length) {
      setFetchedOperators(false);
    } else if (tabKey === "activities" && !activities?.length) {
      setFetchedActivities(false);
    } else if (tabKey === "products" && !products?.length) {
      setFetchedProducts(false);
    } else if (tabKey === "fuelTypes" && !fuelTypes?.length) {
      setFetchedFuelTypes(false);
    } else if (tabKey === "zones" && !zones?.length) {
      setFetchedZones(false);
    }
  }, [tanks?.length, suppliers?.length, vehicles?.length, vehicleGroups?.length, operators?.length, activities?.length, products?.length, fuelTypes?.length, zones?.length]);

  const onChange = (active: number, tabKey: string) => {
    setActiveTab(active);
    triggerFetchForTab(tabKey);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tabKey);
    if (newParams.has("domain")) {
      newParams.delete("domain");
    }
    if (newParams.has("editId")) {
      newParams.delete("editId");
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleResults = <T extends { name?: string; fullName?: string; zoneName?: string }>(
    documentSnapshots: QuerySnapshot<DocumentData>,
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    sortKey?: keyof T & string
  ) => {
    let entries: T[] = [];
    documentSnapshots?.forEach?.((doc) =>
      entries.push({ id: doc.id, ...doc.data() } as unknown as T)
    );
    if (sortKey) {
      entries = entries.sort((a, b) =>
        String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""))
      );
    } else {
      entries = entries.sort((a, b) =>
        String(a.name || a.fullName || a.zoneName || "").localeCompare(String(b.name || b.fullName || b.zoneName || ""))
      );
    }
    setter(entries);
  };

  const buildQuery = (templateName: string) => {
    return query(
      collection(db, templateName),
      where("companyId", "==", companyId)
    );
  };

  useEffect(() => {
    if (!fetchedTanks) {
      const fetchTanks = async () => {
        setFetchedTanks(true);
        const documentSnapshots = await getDocs(buildQuery("tanks"));
        handleResults(documentSnapshots, setTanks);
      };
      fetchTanks();
    }
    if (!fetchedSuppliers) {
      const fetchSuppliers = async () => {
        setFetchedSuppliers(true);
        const documentSnapshots = await getDocs(buildQuery("suppliers"));
        handleResults(documentSnapshots, setSuppliers);
      };
      fetchSuppliers();
    }
    if (!fetchedVehicles) {
      const fetchVehicles = async () => {
        setFetchedVehicles(true);
        const documentSnapshots = await getDocs(buildQuery("fleet"));
        handleResults(documentSnapshots, setVehicles);
      };
      fetchVehicles();
    }
    if (!fetchedVehicleGroups) {
      const fetchVehicleGroups = async () => {
        setFetchedVehicleGroups(true);
        const documentSnapshots = await getDocs(buildQuery("fleetGroups"));
        handleResults(documentSnapshots, setVehicleGroups);
      };
      fetchVehicleGroups();
    }
    if (!fetchedOperators) {
      const fetchOperators = async () => {
        setFetchedOperators(true);
        const documentSnapshots = await getDocs(buildQuery("fleetOperators"));
        handleResults(documentSnapshots, setOperators, "fullName");
      };
      fetchOperators();
    }
    if (!fetchedActivities) {
      const fetchActivities = async () => {
        setFetchedActivities(true);
        const documentSnapshots = await getDocs(buildQuery("activities"));
        handleResults(documentSnapshots, setActivities);
      };
      fetchActivities();
    }
    if (!fetchedProducts) {
      const fetchProducts = async () => {
        setFetchedProducts(true);
        const documentSnapshots = await getDocs(buildQuery("products"));
        handleResults(documentSnapshots, setProducts);
      };
      fetchProducts();
    }
    if (!fetchedFuelTypes) {
      const fetchFuelTypes = async () => {
        setFetchedFuelTypes(true);
        const documentSnapshots = await getDocs(buildQuery("fuelTypes"));
        handleResults(documentSnapshots, setFuelTypes);
      };
      fetchFuelTypes();
    }
    if (!fetchedZones) {
      const fetchZones = async () => {
        setFetchedZones(true);
        const documentSnapshots = await getDocs(buildQuery("areaForFleetUse"));
        handleResults(documentSnapshots, setZones, "zoneName");
      };
      fetchZones();
    }
  }, [
    fetchedTanks,
    fetchedSuppliers,
    fetchedVehicles,
    fetchedVehicleGroups,
    fetchedOperators,
    fetchedActivities,
    fetchedProducts,
    fetchedFuelTypes,
    fetchedZones,
  ]);

  // Handle URL search parameter changes
  useEffect(() => {
    const resolved = resolveAssetTab(searchParams.get("tab")) ?? resolveAssetTab(searchParams.get("domain"));
    if (resolved) {
      if (resolved.index !== activeTab) {
        setActiveTab(resolved.index);
      }
      triggerFetchForTab(resolved.tabKey);
    }
  }, [searchParams, triggerFetchForTab]);

  // Initial fetch when companyId is set
  useEffect(() => {
    if (companyId) {
      setFetchedVehicles(false); 
      setFetchedVehicleGroups(false);
      setFetchedFuelTypes(false);

      const resolved = resolveAssetTab(searchParams.get("tab")) ?? resolveAssetTab(searchParams.get("domain"));
      if (resolved) {
        triggerFetchForTab(resolved.tabKey);
      }
    }
  }, [companyId, triggerFetchForTab]);

  return (
    <>
      {companyId && (
        <>
          <PageHeader
            title="Assets"
            subtitle="Manage fleet vehicles, operators, vehicle groups, tanks, and suppliers"
          />
          <ScrollableTabs
            active={activeTab}
            onTabChange={onChange}
            style={{ width: "100%" }}
            color="cap-navy"
          >
            <TabItem color="cap-navy" label="Vehicles" tabKey="vehicles">
              <VehicleTable elements={vehicles} auth={resolvedAuth} reloadCallback={() => setFetchedVehicles(false)} vehicleGroups={[...vehicleGroups, { id: null as unknown as string, name: "(no group)", companyId: "", description: "" } as VehicleGroup]} fuelTypes={fuelTypes}/>
            </TabItem>
            <TabItem color="cap-navy" label="Fleet operators" tabKey="vehicleOperators">
              <VehicleOperatorsTable elements={operators} auth={resolvedAuth} reloadCallback={() => setFetchedOperators(false)} />
            </TabItem>
            <TabItem color="cap-navy" label="Vehicle groups" tabKey="vehicleGroups">
              <VehicleGroupsTable elements={vehicleGroups} auth={resolvedAuth} reloadCallback={() => setFetchedVehicleGroups(false)} />
            </TabItem>
            <TabItem color="cap-navy" label="Tanks" tabKey="tanks" >
              <TankTable elements={tanks} auth={resolvedAuth} reloadCallback={() => setFetchedTanks(false)} fuelTypes={fuelTypes}/>
            </TabItem>
            <TabItem color="cap-navy" label="Suppliers" tabKey="suppliers">
              <SupplierTable elements={suppliers} auth={resolvedAuth} reloadCallback={() => setFetchedSuppliers(false)}  />
            </TabItem>
            <TabItem color="cap-navy" label="Activities" tabKey="activities">
              <ActivitiesTable elements={activities} auth={resolvedAuth} reloadCallback={() => setFetchedActivities(false)} />
            </TabItem>
            <TabItem color="cap-navy" label="Products" tabKey="products">
              <ProductsTable elements={products} auth={resolvedAuth} reloadCallback={() => setFetchedProducts(false)} />
            </TabItem>
            <TabItem color="cap-navy" label="Fuel types" tabKey="fuelTypes">
              <FuelTypesTable elements={fuelTypes} auth={resolvedAuth} reloadCallback={() => setFetchedFuelTypes(false)} />
            </TabItem>
            <TabItem color="cap-navy" label="Zones" tabKey="zones">
              <ZonesTable elements={zones} auth={resolvedAuth} reloadCallback={() => setFetchedZones(false)}  />
            </TabItem>
          </ScrollableTabs>
          {message && (
            <Notification
              icon={<InfoCircle size={18} />}
              color="cap-blue"
              title="Info"
              style={{
                position: "absolute",
                bottom: "20px",
                right: "20px",
                zIndex: 1000,
              }}
              onClose={() => setMessage("")}
            >
              {message}
            </Notification>
          )}
        </>
      )}
    </>
  );
}
